import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContainer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAgents, useAnalyticsData } from '../hooks/useDashboard';

const Analytics: React.FC = () => {
  const { user } = useAuth();
  const { showError } = useToast();
  const [timeframe, setTimeframe] = useState('week');
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

  // React Query hooks
  const { data: agentsData, isLoading: agentsLoading, error: agentsError } = useAgents();
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useAnalyticsData(selectedAgent, timeframe);

  const agents = useMemo(() => agentsData?.agents || [], [agentsData?.agents]);
  const analytics = analyticsData?.analytics || null;

  // Auto-select first agent when agents load
  React.useEffect(() => {
    if (agents.length > 0 && !selectedAgent) {
      setSelectedAgent(agents[0].id);
    }
    // If selected agent no longer exists, select first available
    if (selectedAgent && agents.length > 0 && !agents.find(agent => agent.id === selectedAgent)) {
      setSelectedAgent(agents[0].id);
    }
    // Clear selection if no agents
    if (agents.length === 0 && selectedAgent) {
      setSelectedAgent(null);
    }
  }, [agents, selectedAgent]);

  // Handle React Query errors
  React.useEffect(() => {
    if (agentsError) {
      showError('Failed to load your agents');
    }
  }, [agentsError, showError]);

  React.useEffect(() => {
    if (analyticsError) {
      const errorMessage = analyticsError.message;
      if (errorMessage.includes('404')) {
        showError('Selected agent no longer exists. Please select another agent.');
        setSelectedAgent(null);
      } else {
        showError('Failed to load analytics data');
      }
    }
  }, [analyticsError, showError]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };


  if (!user?.hasActiveSubscription && !user?.isInGracePeriod) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Analytics Unavailable</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">Subscribe to Svarra Complete to access detailed analytics</p>
          {/* BETA MODE: Subscription disabled
          <a
            href="/#pricing"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-105"
          >
            Subscribe Now
          </a> */}
          <button
            disabled={true}
            className="inline-flex items-center px-6 py-3 bg-gray-400 dark:bg-gray-600 text-white font-medium rounded-lg cursor-not-allowed opacity-60"
          >
            <span className="flex items-center">
              Coming Soon
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    );
  }

  // Now using inline conditional rendering instead of early returns

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            Analytics
            <span className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              BETA
            </span>
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Track your AI assistant's performance and customer interactions
          </p>
        </div>

        {/* Controls */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Agent
            </label>
            <select
              value={selectedAgent || ''}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={agents.length === 0}
            >
              {agents.length === 0 ? (
                <option value="">No agents available</option>
              ) : (
                agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.businessName}
                  </option>
                ))
              )}
            </select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Period
            </label>
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
        </div>

        {/* Content Area */}
        {agentsLoading ? (
          <div className="text-center py-12">
            <LoadingSpinner size="lg" text="Loading agents..." />
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Agents Available</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Create an agent first to view analytics</p>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-105"
            >
              Go to Dashboard
            </Link>
          </div>
        ) : analyticsLoading && selectedAgent ? (
          <div className="text-center py-12">
            <LoadingSpinner size="lg" text="Loading analytics data..." />
          </div>
        ) : analytics ? (
          <>
            {/* Date Range */}
            <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
              Data from {formatDate(analytics?.dateRange?.start || '')} to {formatDate(analytics?.dateRange?.end || '')}
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {analytics?.metrics?.totalBookings?.count || 0}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Total Bookings</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {analytics?.metrics?.totalConversations?.count || 0}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Conversations</p>
                    <p className="text-xs text-blue-600">{analytics?.metrics?.totalConversations?.totalMessages || 0} messages</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {analytics?.metrics?.averageConversationDuration?.averageMinutes || 0}m
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Avg Duration</p>
                    <p className="text-xs text-purple-600">{analytics?.metrics?.averageConversationDuration?.totalMinutes || 0}m total</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {analytics?.metrics?.estimatedStaffTimeSaved?.totalHours || 0}h
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Time Saved</p>
                    <p className="text-xs text-orange-600">{analytics?.metrics?.estimatedStaffTimeSaved?.totalMinutes || 0} minutes</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Most Booked Services */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-80 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Most Booked Services</h3>
                <div className="flex-1 overflow-y-auto">
                  {analytics?.metrics?.mostBookedServices?.length > 0 ? (
                    <div className="space-y-4 pr-2">
                      {analytics.metrics.mostBookedServices.map((service, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-gray-900 dark:text-white font-medium truncate mr-4">{service?.service || 'Unknown Service'}</span>
                          <span className="text-gray-600 dark:text-gray-300 flex-shrink-0">{service?.bookings || 0} bookings</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No bookings recorded yet</p>
                  )}
                </div>
              </div>

              {/* Common Questions */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-80 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Common Question Categories</h3>
                <div className="flex-1 overflow-y-auto">
                  {analytics?.metrics?.commonQuestions?.length > 0 ? (
                    <div className="space-y-4 pr-2">
                      {analytics.metrics.commonQuestions.map((category, index) => (
                        <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-900 dark:text-white font-medium capitalize truncate mr-4">{category?.category || 'Unknown Category'}</span>
                            <span className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">{category?.count || 0} questions</span>
                          </div>
                          {category?.topQuestions?.slice(0, 2).map((question, qIndex) => (
                            <p key={qIndex} className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              "{question?.question || 'Question not available'}"
                            </p>
                          )) || null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No questions categorized yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Unknown Questions - Bot couldn't answer - Full Width */}
            <div className="mt-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 flex flex-col border-2 border-amber-200 dark:border-amber-900">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="text-amber-500">⚠️</span>
                    Questions Bot Couldn't Answer
                  </h3>
                  {analytics?.metrics?.unknownQuestions?.length > 0 && (
                    <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-medium rounded-full">
                      {analytics.metrics.unknownQuestions.length}
                    </span>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {analytics?.metrics?.unknownQuestions?.length > 0 ? (
                    <div className="space-y-3 pr-2">
                      {analytics.metrics.unknownQuestions.map((item, index) => (
                        <div key={index} className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                          <p className="text-sm text-gray-900 dark:text-white font-medium mb-2 line-clamp-2">
                            "{item?.question || 'Question not available'}"
                          </p>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">
                              {item?.customerName || 'Anonymous'}
                            </span>
                            <div className="flex items-center gap-2">
                              {item?.forwarded ? (
                                <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                  ✓ Forwarded
                                </span>
                              ) : (
                                <span className="text-amber-600 dark:text-amber-400">
                                  Pending
                                </span>
                              )}
                              <span className="text-gray-500">
                                {item?.timestamp ? formatDate(item.timestamp) : 'N/A'}
                              </span>
                            </div>
                          </div>
                          {item?.customerContact && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Contact: {item.customerContact}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">✨</div>
                      <p className="text-gray-500 dark:text-gray-400">Great news! Your bot answered all customer questions</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* After Hours Activity & Traffic Sources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
              {/* After Hours Activity */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-64 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">After Hours Activity</h3>
                <div className="flex-1 flex flex-col justify-center">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-300">After Hours Conversations</span>
                      <span className="text-gray-900 dark:text-white font-semibold">
                        {analytics?.metrics?.afterHoursConversations?.count || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-300">After Hours Messages</span>
                      <span className="text-gray-900 dark:text-white font-semibold">
                        {analytics?.metrics?.afterHoursConversations?.totalMessages || 0}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                      After hours: 6:00 PM - 9:00 AM
                    </div>
                  </div>
                </div>
              </div>

              {/* Entry Sources */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-64 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Traffic Sources</h3>
                <div className="flex-1 overflow-y-auto">
                  {analytics?.metrics?.entrySources?.length > 0 ? (
                    <div className="space-y-4 pr-2">
                      {analytics.metrics.entrySources.map((source, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-gray-900 dark:text-white font-medium capitalize truncate mr-4">{source?.source || 'Unknown Source'}</span>
                          <span className="text-gray-600 dark:text-gray-300 flex-shrink-0">{source?.count || 0} visits</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No traffic data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Service Analytics Section */}
            {analytics?.metrics?.serviceAnalytics && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Service Performance</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Most Mentioned Services */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-96 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Most Discussed Services</h3>
                    <div className="flex-1 overflow-y-auto">
                      {analytics?.metrics?.serviceAnalytics?.serviceMentions?.length > 0 ? (
                        <div className="space-y-4 pr-2">
                          {analytics.metrics.serviceAnalytics.serviceMentions.map((service, index) => (
                            <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-gray-900 dark:text-white font-medium capitalize truncate mr-4">{service?.service || 'Unknown Service'}</span>
                                <span className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">{service?.mentions || 0} mentions</span>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span className="truncate mr-2">{service?.conversationCount || 0} conversations</span>
                                <span className="flex items-center flex-shrink-0">
                                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                  {Math.round((service?.avgConfidence || 0) * 100)}% confidence
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400">No service mentions detected yet</p>
                      )}
                    </div>
                  </div>

                  {/* Service Conversion Rates */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 h-96 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Service Booking Conversion</h3>
                    <div className="flex-1 overflow-y-auto">
                      {analytics?.metrics?.serviceBookingConversion?.length > 0 ? (
                        <div className="space-y-4 pr-2">
                          {analytics.metrics.serviceBookingConversion.map((service, index) => (
                            <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-gray-900 dark:text-white font-medium capitalize truncate mr-4">{service?.service || 'Unknown Service'}</span>
                                <span className="text-sm font-semibold text-green-600 flex-shrink-0">{service?.conversionRate || 0}%</span>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                                <span className="truncate mr-2">{service?.conversationCount || 0} inquiries</span>
                                <span className="flex-shrink-0">{service?.bookingConversations || 0} bookings</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div 
                                  className="bg-green-500 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${Math.min(service?.conversionRate || 0, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400">No service conversion data available</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Insights Summary */}
                <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 sm:p-6 min-h-[12rem] flex flex-col">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex-shrink-0">Service Insights</h3>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 w-full">
                      <div className="text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">
                          {analytics?.metrics?.serviceAnalytics?.serviceMentions?.length || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-tight px-2">Unique Services Discussed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">
                          {analytics?.metrics?.serviceAnalytics?.serviceMentions?.reduce((total, service) => total + (service?.mentions || 0), 0) || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-tight px-2">Total Service Mentions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2">
                          {analytics?.metrics?.serviceAnalytics?.serviceInquiries?.totalInquiries || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-tight px-2">Service Inquiries</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-300">Select an agent to view analytics</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;